import { mutatePublicData } from "@/core/api/api-methods";
import { endpoints } from "@/core/api/endpoints";
import { unregisterNotificationDeviceBestEffort } from "@/core/notifications/notification-devices";
import { useSessionStore } from "@/core/stores/session.store";

import { hasAppAccess } from "./roles";

/**
 * Flux « password » du back-office (guide API v1.3) : login / refresh /
 * logout passent par les endpoints PUBLICS du user-service
 * (`/gw/user/api/v1/Auth/*`), qui relaient la demande à Keycloak.
 * L'application n'a besoin que de l'URL du gateway : ni l'URL du realm, ni
 * le client_id, ni le client secret ne sortent du serveur — le token
 * endpoint Keycloak n'est plus appelé directement.
 *
 * ⚠️ En production, repasser en Authorization Code + PKCE avec un client
 * public (mode SSO — voir `keycloak.ts`), seul mode conforme au guide §3.4.
 *
 * Mode choisi par VITE_AUTH_MODE explicite (`password` | `sso`, défaut `sso`).
 */

export type AuthMode = "sso" | "password";

export const AUTH_MODE: AuthMode =
  import.meta.env.VITE_AUTH_MODE === "password" ? "password" : "sso";

export const isPasswordGrantMode = () => AUTH_MODE === "password";

/**
 * Réponse camelCase de `Auth/login` / `Auth/refresh` — miroir local de
 * `UserServiceTokenResponse` (`features/auth/api/dto.ts`) : core n'importe
 * pas les features, et seuls ces deux champs sont consommés ici.
 */
interface GatewayTokenResponse {
  accessToken: string;
  refreshToken?: string;
}

/** Décode le payload JWT (userInfo = claims du token, comme keycloak-js). */
const decodeTokenPayload = (token: string): Record<string, unknown> | null => {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
};

function syncSessionFromTokenResponse(data: GatewayTokenResponse) {
  const claims = decodeTokenPayload(data.accessToken);
  useSessionStore.getState().setSession({
    accessToken: data.accessToken,
    refreshToken: data.refreshToken ?? null,
    // expiresIn = timestamp epoch ABSOLU (claim exp), comme dans keycloak.ts.
    expiresIn: (claims?.exp as number | undefined) ?? null,
    userInfo: claims,
  });
}

/**
 * Erreur dédiée : l'authentification a réussi mais le compte n'a aucun rôle
 * back-office (utilisateur self-service, rôle `default-roles-flash-pay`
 * seul) — il est rejeté avant d'atteindre le dashboard.
 */
export class BackofficeAccessDeniedError extends Error {
  constructor() {
    super("backoffice access denied: no flashpay-* realm role");
    this.name = "BackofficeAccessDeniedError";
  }
}

/** Révocation best-effort du refresh token courant (Auth/logout, 204). */
async function revokeRefreshToken() {
  const refreshToken = useSessionStore.getState().refreshToken;
  if (!refreshToken) return;
  try {
    await mutatePublicData({
      url: endpoints.auth.logout,
      reqBody: { refreshToken },
      method: "POST",
    });
  } catch {
    /* best-effort : le nettoyage local suffit */
  }
}

/**
 * Rejette la session si le compte n'a aucun rôle back-office : révocation
 * du refresh token, nettoyage local, puis erreur dédiée.
 */
async function denyBackofficeAccess(): Promise<never> {
  stopTokenRefresh();
  await revokeRefreshToken();
  useSessionStore.getState().clearSession();
  throw new BackofficeAccessDeniedError();
}

/** Login username + password contre `Auth/login` (endpoint public, JSON). */
export async function loginWithPassword({
  username,
  password,
}: {
  username: string;
  password: string;
}): Promise<void> {
  const data = (await mutatePublicData({
    url: endpoints.auth.login,
    reqBody: { username, password },
    method: "POST",
  })) as GatewayTokenResponse;
  if (!data?.accessToken) throw new Error("login response without accessToken");
  syncSessionFromTokenResponse(data);
  // Nouveau login = re-vérification OTP requise (gate RequireAuth → /verify-otp).
  useSessionStore.getState().setOtpVerified(false);
  if (!hasAppAccess()) await denyBackofficeAccess();
  startTokenRefresh();
}

/* ------------------------------ rafraîchissement ------------------------------ */

/** Intervalle (s) entre deux tentatives de rafraîchissement du token. */
const TOKEN_REFRESH_INTERVAL_SECONDS = 30;
/** Le token est rafraîchi s'il expire dans moins de N secondes. */
const TOKEN_MIN_VALIDITY_SECONDS = 60;

let refreshTimer: ReturnType<typeof setInterval> | null = null;

async function refreshSession(): Promise<boolean> {
  const refreshToken = useSessionStore.getState().refreshToken;
  if (!refreshToken) return false;
  try {
    const data = (await mutatePublicData({
      url: endpoints.auth.refresh,
      reqBody: { refreshToken },
      method: "POST",
    })) as GatewayTokenResponse;
    if (!data?.accessToken) return false;
    syncSessionFromTokenResponse(data);
    return true;
  } catch {
    return false;
  }
}

export function startTokenRefresh() {
  if (refreshTimer) return;
  refreshTimer = setInterval(() => {
    const expiresAt = useSessionStore.getState().expiresIn;
    const now = Math.floor(Date.now() / 1000);
    if (expiresAt && expiresAt - TOKEN_MIN_VALIDITY_SECONDS > now) return;
    void refreshSession().then((ok) => {
      if (!ok) {
        // Refresh token expiré/révoqué (401) : session nettoyée, la
        // redirection vers /login est gérée par RequireAuth / le hook 401.
        stopTokenRefresh();
        useSessionStore.getState().clearSession();
      }
    });
  }, TOKEN_REFRESH_INTERVAL_SECONDS * 1000);
}

export function stopTokenRefresh() {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

/**
 * Restauration au boot (mode password) : si un refresh token persisté
 * existe, on tente un rafraîchissement immédiat ; sinon session invalide.
 * Un compte qui n'a (plus) aucun rôle back-office est rejeté ici aussi.
 */
export async function restorePasswordSession(): Promise<boolean> {
  const { refreshToken, expiresIn } = useSessionStore.getState();
  if (!refreshToken) return false;
  const now = Math.floor(Date.now() / 1000);
  const valid =
    expiresIn && expiresIn - TOKEN_MIN_VALIDITY_SECONDS > now
      ? true
      : await refreshSession();
  if (!valid) {
    useSessionStore.getState().clearSession();
    return false;
  }
  if (!hasAppAccess()) {
    await revokeRefreshToken();
    useSessionStore.getState().clearSession();
    return false;
  }
  startTokenRefresh();
  return true;
}

/**
 * Logout (mode password) : révocation du refresh token via `Auth/logout`
 * (204 idempotent, best-effort — toute erreur est ignorée), puis nettoyage
 * local et retour au login.
 */
export async function passwordLogout() {
  await unregisterNotificationDeviceBestEffort();
  await revokeRefreshToken();
  stopTokenRefresh();
  useSessionStore.getState().clearSession();
  window.location.assign("/login");
}
